// src/modules/Printers/hooks/useEditPrinterModal.ts
import { useRef, useState } from 'react';
import { useToast } from '@shared/hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { PrintersApiAdapter } from '../adapters/PrintersApiAdapter';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services';

export const useEditPrinterModal = (onUpdated?: () => void) => {
  const { t } = useTranslation('printersModule');
  const { toast } = useToast();
  const modalRef = useRef<ModalCenterRef>(null);
  const api = new PrintersApiAdapter(createHttpClient(apiClientWH));

  const [isLoading, setIsLoading] = useState(false);
  const [printerCode, setPrinterCode] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [fallbackName, setFallbackName] = useState('');

  const openModal = (printer: {
    id: number;
    code?: string;
    name: string;
    type: string;
    custom_name?: string | null;
  }) => {
    // Prioriza `code` (según contrato API) y mantiene fallback a `id` para compatibilidad.
    setPrinterCode(printer.code || String(printer.id));
    setName(printer.custom_name || '');
    setFallbackName(printer.name);
    modalRef.current?.openModal();
  };

  const closeModal = () => {
    modalRef.current?.closeModal();
  };

  const handleSave = async () => {
    if (!printerCode) {
      toast({
        title: t('toast.error_title'),
        description: t('toast.error_description'),
        variant: 'destructive',
      });
      return;
    }

    setIsLoading(true);
    try {
      const resolvedName = (name || fallbackName).trim();
      await api.updatePrinter(printerCode, resolvedName);
      toast({
        title: t('toast.success_title'),
        description: t('toast.success_description'),
      });
      closeModal();
      onUpdated?.();
    } catch (error) {
      toast({
        title: t('toast.error_title'),
        description: t('toast.error_description'),
        variant: 'destructive',
      });
      console.error('❌ Error al editar impresora:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    modalRef,
    openModal,
    closeModal,
    handleSave,
    name,
    setName,
    fallbackName,
    isLoading,
  };
};
